/* eslint-disable react/style-prop-object */
import React, {useCallback, useMemo, useState} from 'react';
import {Button, TableCell, TableRow} from '@material-ui/core';
import {ArrowDropDownOutlined, ArrowDropUpOutlined} from '@material-ui/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import cn from 'classnames';
import {StudioKebabMenu} from '../../../../base-components/StudioKebabMenu';
import {StudioCheckbox} from '../../../../base-components/StudioCheckbox';
import {
  AddOrUpdateDeviceGroup,
  DeviceGroupInfo,
} from '../../../../types/deployment/DeploymentDeviceGroup';
import {StudioTextField} from '../../../../base-components/StudioTextField';
import {StudioSelect} from '../../../../base-components/StudioSelect';
import {DeleteConfirmationData, DeviceGroupInfoRow} from '.';
import {useDeviceGroupNodes} from '../useDeviceGroupNodes';
import {DeviceStatus} from '../../DeploymentStatus/DeviceStatus';

type DeviceTableRowType = {
  row: DeviceGroupInfoRow;
  deviceGroups: (DeviceGroupInfo & {selected: boolean})[];
  handleDownloadDeviceGroup: (ids: string[]) => void;
  setDeviceGroups: (devices: DeviceGroupInfoRow[]) => void;
  setDeleteConfirmation: (data: DeleteConfirmationData) => void;
  handleUpdateDeviceGroup: (data: AddOrUpdateDeviceGroup) => void;
  setIsEditing: (value: boolean | ((value: boolean) => boolean)) => void;
};

const DeviceGroupTableRow = ({
  row,
  deviceGroups,
  handleDownloadDeviceGroup,
  setDeviceGroups,
  setDeleteConfirmation,
  handleUpdateDeviceGroup,
  setIsEditing,
}: DeviceTableRowType) => {
  const intl = useIntl();
  const NODES_DISPLAYED_LIMIT = 10;
  const devices = row?.nodes?.length || 0;
  const [isEdit, setIsEdit] = useState(false);
  const [collapseDevices, setCollapseDevice] = useState(true);
  const [formDetails, setFormDetails] = useState({
    id: row.id,
    name: row.name,
    description: row.description,
    status: row.status,
    nodes: row?.nodes?.map(n => n.id) || [],
  });

  const [offlineFlag, setOfflineFlag] = useState(false);
  const [onlineFlag, setOnlineFlag] = useState(false);

  const {data: response} = useDeviceGroupNodes({});

  const deviceGroupNodes = useMemo(() => response?.flatMap(n => n?.body?.entries) || [], [
    response,
  ]);

  const data = useMemo(
    () =>
      offlineFlag && onlineFlag
        ? deviceGroupNodes
        : offlineFlag
        ? deviceGroupNodes.filter(n => n.status === 'OFFLINE')
        : onlineFlag
        ? deviceGroupNodes.filter(n => n.status === 'ONLINE')
        : deviceGroupNodes,
    [deviceGroupNodes, offlineFlag, onlineFlag]
  );

  const options = (data || []).reduce((acc: Record<string, string>, curr) => {
    acc[curr.id] = curr.name;
    return acc;
  }, {});

  const allOptions = (deviceGroupNodes || []).reduce(
    (acc: Record<string, string>, curr) => {
      acc[curr.id] = curr.name;
      return acc;
    },
    {}
  );

  const toggleCollapseDevices = () => {
    setCollapseDevice(!collapseDevices);
  };
  const toggleIsEdit = () => {
    setIsEditing(prev => !prev);
    setIsEdit(!isEdit);
  };

  const handleChange = (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormDetails({
      ...formDetails,
      [event.target.name]: event.target.value,
    });
  };

  const renderStartAdornment = useCallback(
    (val: string) => {
      const status = deviceGroupNodes.find(d => d.id === val)?.status;
      return status && <DeviceStatus status={status} showLabel={false} />;
    },
    [deviceGroupNodes]
  );

  return !isEdit ? (
    <TableRow key={row.id} className={cn('prod-deploy-dashboard__row')}>
      <TableCell>
        <StudioCheckbox
          checked={row.selected}
          name="check"
          onChange={(_, checked) => {
            const newDeviceGroups = deviceGroups.map(e => {
              return {
                ...e,
                selected: row.id === e.id ? checked : e.selected,
              };
            });
            setDeviceGroups(newDeviceGroups);
          }}
        />
      </TableCell>
      <TableCell>
        <div
          className="prod-deploy-dashboard__table_cell_center"
          onClick={toggleCollapseDevices}
        >
          <span>{row.name}</span>
          {devices > 1 && (
            <span>
              {collapseDevices ? <ArrowDropDownOutlined /> : <ArrowDropUpOutlined />}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell size="medium">
        <DeviceStatus status={row.status} showLabel={false} />
      </TableCell>
      <TableCell>
        {row?.nodes ? (
          collapseDevices ? (
            <DeviceStatus
              status={row.status}
              label={row?.nodes?.[0]?.name}
              capitalize={false}
            />
          ) : (
            <>
              {row.nodes.slice(0, NODES_DISPLAYED_LIMIT).map(({name, status}) => (
                <DeviceStatus
                  status={status}
                  label={name}
                  className="prod-deploy-dashboard__device_status"
                  capitalize={false}
                />
              ))}
              {devices > NODES_DISPLAYED_LIMIT && (
                <p>
                  {intl.formatMessage({
                    id: 'prodDeployment.deviceGroupViewMore',
                  })}
                </p>
              )}
            </>
          )
        ) : (
          <p>
            {intl.formatMessage({
              id: 'prodDeployment.deviceGroupNoNodesAvailable',
            })}
          </p>
        )}
      </TableCell>
      <TableCell>
        {row?.deploymentStatus ? (
          <DeviceStatus
            status={row.deploymentStatus}
            label={row.deploymentStatus?.replace('_', ' ')}
            capitalize
          />
        ) : (
          <p>
            {intl.formatMessage({
              id: 'prodDeployment.not.started.empty.field',
            })}
          </p>
        )}
      </TableCell>
      <TableCell>
        <div className="prod-deploy-dashboard__table_cell_center">
          <span>
            {row.deploymentName
              ? row.deploymentName
              : intl.formatMessage({
                  id: 'prodDeployment.not.started.empty.field',
                })}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <div className="prod-deploy-dashboard__table_cell_center">
          <span>
            {row.applicationName
              ? row.applicationName
              : intl.formatMessage({
                  id: 'prodDeployment.not.started.empty.field',
                })}
          </span>
        </div>
      </TableCell>
      <TableCell>
        <div className="prod-deploy-dashboard__actions">
          <StudioKebabMenu
            items={[
              {
                title: intl.formatMessage({
                  id: 'prodDeployment.deviceGroupDownload',
                }),
                onClick: () => handleDownloadDeviceGroup([row.id]),
              },
              {
                title: intl.formatMessage({
                  id: 'prodDeployment.deviceGroupEdit',
                }),
                onClick: toggleIsEdit,
              },
              {
                title: intl.formatMessage({id: 'prodDeployment.delete'}),
                onClick: () =>
                  setDeleteConfirmation({
                    isOpen: true,
                    deviceGroup: row,
                    isMultiple: false,
                    selectedIds: [],
                  }),
              },
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  ) : (
    <TableRow
      key={row.id}
      className={cn('prod-deploy-dashboard__row', 'prod-deploy-dashboard__')}
    >
      <TableCell>
        <StudioCheckbox
          checked={row.selected}
          name="check"
          onChange={(_, checked) => {
            const newDeviceGroups = deviceGroups.map(e => {
              return {
                ...e,
                selected: row.id === e.id ? checked : e.selected,
              };
            });
            setDeviceGroups(newDeviceGroups);
          }}
        />
      </TableCell>
      <TableCell className="prod-deploy-dashboard__edit">
        <StudioTextField
          placeholder={intl.formatMessage({
            id: 'projectPanel.descriptionPlaceholder',
          })}
          name="name"
          margin="normal"
          variant="filled"
          id="name"
          fullWidth
          multiline={false}
          data-testid="edit-device-group-name"
          useFieldProps={false}
          value={formDetails.name}
          strategy="studio"
          onChange={handleChange}
          onBlur={handleChange}
        />
      </TableCell>
      <TableCell />
      <TableCell className="prod-deploy-dashboard__edit">
        <StudioSelect
          options={options}
          allOptions={allOptions}
          SelectProps={{
            value: formDetails.nodes,
            onChange: event => {
              setFormDetails({
                ...formDetails,
                nodes: event.target.value as string[],
              });
            },
            multiple: true,
          }}
          placeholder={intl.formatMessage({
            id: 'prodDeployment.deviceGroupSelectDevices',
          })}
          search
          longMenuContainer
          renderStartAdornment={renderStartAdornment}
          showDone={false}
          onChipDelete={val => {
            setFormDetails({
              ...formDetails,
              nodes: formDetails.nodes.filter(n => n !== val),
            });
          }}
          additionalMenu={
            <div
              className="prod-deploy-dashboard__status"
              data-testid="studio-select-item"
            >
              <span
                onClickCapture={e => {
                  e.stopPropagation();
                  setOnlineFlag(!onlineFlag);
                }}
              >
                <StudioCheckbox
                  checked={onlineFlag}
                  name="check"
                  label="Online"
                  onClick={e => {
                    e.stopPropagation();
                    setOnlineFlag(!onlineFlag);
                  }}
                />
              </span>
              <span
                onClickCapture={e => {
                  e.stopPropagation();
                  setOfflineFlag(!offlineFlag);
                }}
              >
                <StudioCheckbox
                  checked={offlineFlag}
                  name="check"
                  label="Offline"
                  onClick={e => {
                    e.stopPropagation();
                    setOfflineFlag(!offlineFlag);
                  }}
                />
              </span>
            </div>
          }
        />
      </TableCell>
      <TableCell colSpan={5}>
        <div className="prod-deploy-dashboard__device_group_edit">
          <Button onClick={toggleIsEdit} size="large" variant="outlined">
            <FormattedMessage id="form.cancel" />
          </Button>
          <Button
            onClick={() => handleUpdateDeviceGroup(formDetails)}
            color="primary"
            size="large"
            variant="contained"
            type="submit"
            data-testid="create-project-page-submit"
            id="create-project-page-submit"
          >
            <FormattedMessage id="prodDeployment.deviceGroupSave" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

export default DeviceGroupTableRow;
